<script lang="ts">
    //STYLE: Classes factorizing tailwind's ones are defined in video-ui.css
    import { getContext, onDestroy, onMount } from "svelte";
    import * as Sentry from "@sentry/svelte";
    import type { Subscription } from "rxjs";
    import SoundMeterWidget from "../SoundMeterWidget.svelte";
    import { highlightedEmbedScreen } from "../../Stores/HighlightedEmbedScreenStore";
    import type { VideoBox } from "../../Space/VideoBox";
    import { LL } from "../../../i18n/i18n-svelte";
    import { analyticsClient } from "../../Administration/AnalyticsClient";
    import loaderImg from "../images/loader.svg";
    import { highlightFullScreen } from "../../Stores/ActionsCamStore";
    import { showFloatingUi } from "../../Utils/svelte-floatingui-show";
    import { userActivationManager } from "../../Stores/UserActivationStore";
    import { triggerClickOnEnterOrSpace } from "../../Utils/svelte-a11y";
    import { displayVideoQualityStore } from "../../Stores/DisplayVideoQualityStore";
    import { requestedMegaphoneStore } from "../../Stores/MegaphoneStore";
    import { requestedCameraState, requestedMicrophoneState } from "../../Stores/MediaStore";
    import { requestedScreenSharingState } from "../../Stores/ScreenSharingStore";
    import { blackListManager } from "../../WebRtc/BlackListManager";
    import { blocker } from "../../Utils/screenBlocker";
    import ActionMediaBox from "./ActionMediaBox.svelte";
    import UserName from "./UserName.svelte";
    import UpDownChevron from "./UpDownChevron.svelte";
    import CenteredVideo from "./CenteredVideo.svelte";
    import WebRtcStats from "./WebRtcStatsBox.svelte";
    import { IconArrowsMinimize, IconArrowsMaximize, IconMicrophoneOff } from "@wa-icons";

    interface Props {
        fullScreen?: boolean;
        videoBox: VideoBox; // If true, and if there is no video, the height of the video box will be 11rem
        miniMode?: boolean;
        activeUserName?: boolean;
    }

    let { fullScreen = false, videoBox, miniMode = false, activeUserName = true }: Props = $props();

    let streamableStore = $derived(videoBox.streamable);
    let streamablesStore = $derived(videoBox.streamables);
    let effectiveStatusStore = $derived(videoBox.statusStore);
    let effectiveStatus = $derived($effectiveStatusStore);
    let streamableEntries = $derived($streamablesStore);
    let activeStreamableEntry = $derived(streamableEntries.find((entry) => !entry.isPending));
    let streamable = $derived(activeStreamableEntry?.streamable ?? $streamableStore);

    // The inCameraContainer is used to know if the VideoMediaBox is part of a series of video or if it is the highlighted video.
    let inCameraContainer: boolean = !!getContext("inCameraContainer");
    let inHighlightFullscreenParticipantList: boolean = !!getContext("inHighlightFullscreenParticipantList");

    let extendedSpaceUser = $derived(videoBox.spaceUser);
    let megaphoneState = $derived(extendedSpaceUser?.reactiveUser.megaphoneState);
    let microphoneStateStore = $derived(extendedSpaceUser?.reactiveUser.microphoneState);

    let pictureStore = $derived(extendedSpaceUser?.pictureStore);

    let name = $derived(extendedSpaceUser?.name);

    let showUserSubMenu = $state(false);

    let hasVideoStore = $derived(streamable?.hasVideo);
    let hasAudioStore = $derived(streamable?.hasAudio);
    let volumeMeterStore = $derived(streamable?.volumeStore);
    let showVoiceIndicatorStore = $derived(streamable?.showVoiceIndicator);
    let isBlockedStore = $derived(streamable?.media?.isBlocked);
    let mediaStreamStore = $derived(
        streamable?.media.type === "webrtc" || streamable?.media.type === "livekit"
            ? streamable.media.streamStore
            : undefined,
    );
    let muteAudioStore = $derived(streamable?.muteAudio);
    let muteAudio = $derived(muteAudioStore ? $muteAudioStore : false);
    let volumeStore = $derived(streamable?.volume);
    let volumeMeter = $derived($volumeMeterStore);
    let webRtcStatsStore = $derived($displayVideoQualityStore ? streamable?.webrtcStats : undefined);
    let webRtcStats = $derived($webRtcStatsStore);

    // Check if user is currently reconnecting (WebRTC retry in progress)
    let isReconnecting = $derived(effectiveStatus === "reconnecting");

    let showVoiceIndicator = $derived(showVoiceIndicatorStore ? $showVoiceIndicatorStore : false);

    // If there is no constraintStore, we are in a screen sharing (so video is enabled)

    let videoEnabled = $derived($hasVideoStore);

    let isMegaphoneSpace = $derived(videoBox.isMegaphoneSpace ?? false);

    // Check if this is the local user's video box
    let isLocalUser = $derived(videoBox.uniqueId === "-1" || extendedSpaceUser?.spaceUserId === "local");
    // Debugging aid for the rare case where the space says the remote microphone is enabled but this receiver has no audio.
    let audioStateMismatch = $derived(
        effectiveStatus === "connected" &&
            streamable?.videoType === "video" &&
            !isLocalUser &&
            $isBlockedStore !== true &&
            $microphoneStateStore === true &&
            $hasAudioStore === false,
    );
    // Like audioStateMismatch, but with a 3 seconds delay.
    let showAudioStateMismatch = $state(false);
    let loggedAudioMismatchKey: string | undefined = undefined;

    $effect(() => {
        if (!audioStateMismatch) {
            return;
        }

        const audioMismatchKey = `${streamable?.uniqueId ?? "unknown"}:${extendedSpaceUser?.spaceUserId ?? "unknown"}:${streamable?.media.type ?? "unknown"}`;
        if (loggedAudioMismatchKey === audioMismatchKey) {
            return;
        }

        const timeout = setTimeout(() => {
            const audioTracks =
                $mediaStreamStore?.getAudioTracks().map((track) => ({
                    id: track.id,
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState,
                })) ?? [];
            const details = {
                streamableUniqueId: streamable?.uniqueId,
                streamableType: streamable?.media.type,
                videoType: streamable?.videoType,
                spaceUserId: extendedSpaceUser?.spaceUserId,
                status: effectiveStatus,
                hasAudio: $hasAudioStore,
                microphoneState: $microphoneStateStore,
                audioTrackCount: audioTracks.length,
                audioTracks,
            };

            console.warn(
                "Audio state mismatch: remote microphone is enabled in the space but no receiver audio track is present",
                details,
            );
            Sentry.captureMessage(
                "Audio state mismatch: remote microphone is enabled in the space but no receiver audio track is present",
                {
                    level: "warning",
                    tags: {
                        component: "VideoMediaBox",
                        streamableType: streamable?.media.type ?? "unknown",
                        videoType: streamable?.videoType ?? "unknown",
                    },
                    extra: details,
                },
            );

            loggedAudioMismatchKey = audioMismatchKey;
            showAudioStateMismatch = true;
        }, 3000);

        return () => {
            clearTimeout(timeout);
            showAudioStateMismatch = false;
        };
    });

    // Check if the local user is streaming with megaphone
    // requestedMegaphoneStore is true when user has requested megaphone
    // We also need to check if they are actually streaming (camera, mic, or screen)
    let isLocalUserStreamingMegaphone = $derived(
        isLocalUser &&
            $requestedMegaphoneStore &&
            ($requestedCameraState || $requestedMicrophoneState || $requestedScreenSharingState),
    );

    let blackListSubject: Subscription | undefined;
    let unBlackListSubject: Subscription | undefined;
    let blackListVersion = $state(0);
    let spaceUserId = $derived(extendedSpaceUser?.spaceUserId);
    let userUuidStore = $derived(extendedSpaceUser?.reactiveUser.uuid);
    let userUuid = $derived($userUuidStore);
    let isCurrentUserBlackListed = $derived(computeIsCurrentUserBlackListed(blackListVersion, spaceUserId, userUuid));

    function computeIsCurrentUserBlackListed(
        _blackListVersion: number,
        currentSpaceUserId: string | undefined,
        currentUserUuid: string | undefined,
    ): boolean {
        return (
            currentSpaceUserId !== undefined &&
            currentSpaceUserId !== "local" &&
            (blackListManager.isBlackListed(currentSpaceUserId) ||
                (currentUserUuid !== undefined && blackListManager.isBlackListed(currentUserUuid)))
        );
    }

    function setFullScreen() {
        exitFullScreen();
        highlightPeer();
        highlightFullScreen.set(true);
    }

    function exitFullScreen() {
        highlightFullScreen.set(false);
    }

    function removeHighlight() {
        highlightedEmbedScreen.removeHighlight();
    }

    let userMenuButton: HTMLDivElement | undefined = $state();

    let closeFloatingUi: (() => void) | undefined;

    function toggleUserMenu() {
        showUserSubMenu = !showUserSubMenu;
        const spaceUser = extendedSpaceUser;
        if (showUserSubMenu && spaceUser && userMenuButton) {
            closeFloatingUi = showFloatingUi(
                userMenuButton,
                // @ts-ignore See https://github.com/storybookjs/storybook/issues/21884
                ActionMediaBox,
                {
                    spaceUser,
                    videoEnabled: videoEnabled ?? false,
                    volumeStore,
                    videoType: streamable?.videoType,
                    onClose: () => {
                        showUserSubMenu = false;
                        closeFloatingUi?.();
                    },
                },
                {
                    placement: "bottom",
                },
                8,
                false,
            );
            // onclose={() => (showUserSubMenu = false)}
        } else {
            closeFloatingUi?.();
            closeFloatingUi = undefined;
        }
    }

    let showAfterDelay = $state(true);
    let connectingTimer: ReturnType<typeof setTimeout> | null = null;
    let markStreamableReadyTimer: ReturnType<typeof setTimeout> | undefined = $state();

    // When the status is "connecting", do not show the loader for 500ms to avoid visual glitches during fast connections.
    // EXCEPT when reconnecting: in that case, show the loader immediately to avoid black screen.
    function updateShowAfterDelay(status: string | undefined, reconnecting: boolean): void {
        if (status === "reconnecting") {
            // Reconnecting: show loader immediately (no delay) to avoid black screen
            showAfterDelay = true;
            if (connectingTimer) {
                clearTimeout(connectingTimer);
                connectingTimer = null;
            }
        } else if (status === "connecting") {
            // Initial connection: wait 500ms before showing loader
            showAfterDelay = false;
            if (connectingTimer) clearTimeout(connectingTimer);
            connectingTimer = setTimeout(() => {
                showAfterDelay = true;
            }, 500);
        } else {
            showAfterDelay = true;
            if (connectingTimer) {
                clearTimeout(connectingTimer);
                connectingTimer = null;
            }
        }
    }

    $effect(() => {
        updateShowAfterDelay(effectiveStatus, isReconnecting);
    });

    function highlightPeer() {
        highlightedEmbedScreen.highlight(videoBox);
        analyticsClient.pinMeetingAction();
        window.focus();
    }

    onMount(() => {
        blackListSubject = blackListManager.onBlockStream.subscribe(() => {
            blackListVersion += 1;
        });
        unBlackListSubject = blackListManager.onUnBlockStream.subscribe(() => {
            blackListVersion += 1;
        });
    });

    onDestroy(() => {
        blackListSubject?.unsubscribe();
        unBlackListSubject?.unsubscribe();
        closeFloatingUi?.();
        if (connectingTimer) clearTimeout(connectingTimer);
        if (markStreamableReadyTimer) clearTimeout(markStreamableReadyTimer);
    });
</script>

<div
    {@attach blocker}
    class="group/screenshare relative flex justify-center mx-auto h-full w-full @container/videomediabox z-20 select-none"
>
    <div
        class={"w-full transition-all bg-center bg-no-repeat " +
            (fullScreen || effectiveStatus !== "connected"
                ? effectiveStatus === "connecting" || effectiveStatus === "reconnecting"
                    ? "bg-gray-700/80 backdrop-blur"
                    : "bg-mlight/80 backdrop-blur"
                : "")}
        style={videoEnabled && effectiveStatus === "connecting" ? "background-image: url(" + loaderImg + ")" : ""}
        class:h-full={videoEnabled || !miniMode}
        class:h-11={!videoEnabled && miniMode}
        class:flex-col={videoEnabled}
        class:items-center={!videoEnabled || effectiveStatus === "connecting" || effectiveStatus === "reconnecting"}
        class:flex-row={!videoEnabled}
        class:relative={!videoEnabled}
        class:rounded-md2={!fullScreen}
        class:justify-center={effectiveStatus === "connecting" || effectiveStatus === "reconnecting"}
    >
        <!-- Status messages based on connection state -->
        {#if (effectiveStatus === "connecting" || effectiveStatus === "reconnecting" || effectiveStatus === "error") && showAfterDelay}
            <!-- Connecting/Reconnecting state: show spinner with appropriate message -->
            <div class="absolute w-full h-full overflow-hidden">
                <div
                    class="flex w-8 h-8 justify-center items-center absolute right-2 top-2 @[22rem]/videomediabox:w-full @[22rem]/videomediabox:right-auto @[22rem]/videomediabox:top-auto @[22rem]/videomediabox:h-full @[22rem]/videomediabox:justify-center @[22rem]/videomediabox:items-center @[22rem]/videomediabox:right-none @[22rem]/videomediabox:top-none"
                >
                    <div class="connecting-spinner"></div>
                </div>
            </div>
            <div class="absolute w-full h-full pointer-events-none">
                <div class="w-full h-full flex flex-col justify-end items-center pb-4">
                    {#if effectiveStatus === "error"}
                        <!-- Persistent issue: show warning message while still reconnecting -->
                        <div class="text-lg text-white font-bold text-center px-4">
                            {$LL.video.persistent_connection_issue()}
                        </div>
                    {:else}
                        <div class="text-lg text-white font-bold">
                            {#if isReconnecting}
                                {$LL.video.reconnecting()}
                            {:else}
                                {$LL.video.connecting()}
                            {/if}
                        </div>
                    {/if}
                </div>
            </div>
        {/if}

        {#if showAfterDelay}
            {#each streamableEntries as streamableEntry (streamableEntry.id)}
                <div
                    class="absolute inset-0"
                    class:pointer-events-none={streamableEntry.isPending}
                    style={streamableEntry.isPending ? "opacity: 0" : undefined}
                >
                    <CenteredVideo
                        media={streamableEntry.streamable.media}
                        videoEnabled={streamableEntry.isPending ? true : (videoEnabled ?? false)}
                        status={streamableEntry.isPending ? "connected" : effectiveStatus}
                        verticalAlign={!inCameraContainer && !inHighlightFullscreenParticipantList && !fullScreen
                            ? "top"
                            : "center"}
                        isTalking={streamableEntry.isPending ? false : (showVoiceIndicator ?? false)}
                        flipX={streamableEntry.streamable.flipX ?? false}
                        cover={streamableEntry.streamable.displayMode === "cover" &&
                            (inCameraContainer || inHighlightFullscreenParticipantList || fullScreen)}
                        isBlocked={streamableEntry.isPending ? false : ($isBlockedStore ?? false)}
                        withBackground={streamableEntry.isPending
                            ? false
                            : ((((inCameraContainer || inHighlightFullscreenParticipantList) &&
                                  effectiveStatus !== "connecting" &&
                                  effectiveStatus !== "reconnecting") ||
                                  $isBlockedStore) ??
                              false)}
                        isMegaphoneSpace={streamableEntry.isPending
                            ? false
                            : (isMegaphoneSpace && $megaphoneState) || isLocalUserStreamingMegaphone || false}
                        onvideo={() => {
                            markStreamableReadyTimer = setTimeout(() => {
                                if (streamableEntry.isPending) {
                                    videoBox.markPendingStreamableReady(streamableEntry.id);
                                }
                                markStreamableReadyTimer = undefined;
                                // Even by waiting the first frame, we see a first glitch.
                                // Maybe the first LiveKit frame is not correctly rendered?
                                // We noticed waiting a bit makes the glitch disappear.
                            }, 200);
                        }}
                    >
                        {#if !streamableEntry.isPending && activeUserName}
                            <UserName
                                name={name ?? "unknown"}
                                picture={pictureStore}
                                isPlayingAudio={showVoiceIndicator ?? false}
                                isCameraDisabled={(!(videoEnabled ?? false) && !miniMode) ||
                                    effectiveStatus !== "connected"}
                                isBlocked={$isBlockedStore ?? false}
                                position={videoEnabled && !$isBlockedStore && effectiveStatus === "connected"
                                    ? "absolute bottom-0 left-0 @[17.5rem]/videomediabox:bottom-2 @[17.5rem]/videomediabox:left-2"
                                    : "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"}
                                grayscale={effectiveStatus === "connecting" || effectiveStatus === "reconnecting"}
                            >
                                {#if extendedSpaceUser && extendedSpaceUser.spaceUserId !== "local"}
                                    <div
                                        class="flex items-center justify-center picture-in-picture:hidden"
                                        bind:this={userMenuButton}
                                    >
                                        <UpDownChevron
                                            enabled={showUserSubMenu}
                                            onclick={toggleUserMenu}
                                            ariaLabel="Меню участника"
                                        />
                                    </div>
                                {/if}
                            </UserName>

                            {#if effectiveStatus === "connected" && !$isBlockedStore}
                                <div
                                    class="z-251 absolute p-2 right-1 text-white"
                                    class:top-1={videoEnabled}
                                    class:top-0={!videoEnabled}
                                >
                                    {#if $hasAudioStore && !audioStateMismatch}
                                        <SoundMeterWidget
                                            volume={volumeMeter}
                                            cssClass="voice-meter-cam-off relative mr-0 ml-auto translate-x-0 transition-transform"
                                            barColor="dark"
                                        />
                                    {:else if streamable?.videoType === "video"}
                                        <IconMicrophoneOff
                                            aria-label={$LL.video.user_is_muted({ name: name ?? "unknown" })}
                                            data-testid={$LL.video.user_is_muted({ name: name ?? "unknown" })}
                                            class="{showAudioStateMismatch
                                                ? 'text-red-500 '
                                                : ''}filter-[drop-shadow(0_0_3px_rgb(0,0,0))_drop-shadow(0_0_6px_rgb(0,0,0))_drop-shadow(0_0_9px_rgb(0,0,0))]"
                                        />
                                    {/if}
                                </div>
                            {/if}
                            {#if webRtcStats}
                                <WebRtcStats {webRtcStats} />
                            {/if}

                            <!-- The menu to go fullscreen -->
                            {#if !inCameraContainer && videoEnabled}
                                <div
                                    class="absolute m-auto top-0 right-0 left-0 h-14 w-fit rounded-lg bg-contrast/50 backdrop-blur transition-all opacity-0 hover:opacity-100! group-hover/centered-video:opacity-20 pointer-coarse:opacity-100 flex items-center justify-center cursor-pointer"
                                >
                                    <div class="h-full w-full flex flex-row justify-evenly cursor-pointer">
                                        {#if !fullScreen && !$highlightFullScreen}
                                            <button
                                                class="svg p-4 h-full w-full hover:bg-white/10 flex justify-start items-center z-25 rounded-lg text-base"
                                                onclick={removeHighlight}
                                            >
                                                <IconArrowsMinimize font-size="20" class="text-white" />
                                            </button>
                                        {/if}
                                        {#if fullScreen}
                                            <button
                                                class="muted-video p-4 h-full w-full hover:bg-white/10 flex justify-start cursor-pointer items-center z-25 rounded-lg text-base"
                                                onclick={exitFullScreen}
                                            >
                                                <IconArrowsMinimize font-size="20" class="text-white" />
                                            </button>
                                        {:else}
                                            <button
                                                class="muted-video p-4 h-full w-full hover:bg-white/10 flex justify-start cursor-pointer items-center z-25 rounded-lg text-base"
                                                onclick={setFullScreen}
                                                data-testid="highlight-enter-fullscreen-button"
                                            >
                                                <IconArrowsMaximize font-size="20" class="text-white" />
                                            </button>
                                        {/if}
                                    </div>
                                </div>
                            {/if}
                        {/if}
                    </CenteredVideo>
                </div>
            {/each}
        {/if}
    </div>

    {#if inCameraContainer && videoEnabled && $isBlockedStore === false}
        {#await userActivationManager.waitForUserActivation()}
            <!-- Waiting for user activation; nothing to show -->
        {:then}
            <button
                class="full-screen-button absolute top-0 bottom-0 right-0 left-0 m-auto h-14 w-14 z-20 p-4 rounded-lg bg-contrast/50 backdrop-blur transition-all opacity-0 group-hover/screenshare:opacity-100 hover:bg-white/10 cursor-pointer"
                onclick={highlightPeer}
                aria-label="Развернуть видео участника"
            >
                <IconArrowsMaximize font-size="20" class="text-white" />
            </button>
        {/await}
    {/if}
    {#if !muteAudio}
        {#await userActivationManager.waitForUserActivation()}
            <div
                class="absolute w-full h-full aspect-video mx-auto flex justify-center items-center bg-contrast/50 rounded-lg z-20 cursor-pointer"
                role="button"
                tabindex="0"
                aria-label={$LL.video.click_to_unmute()}
                onclick={() => {
                    userActivationManager.notifyUserActivation();
                }}
                onkeydown={triggerClickOnEnterOrSpace}
            >
                <div class="text-center">
                    <div class="text-lg text-white bold">{$LL.video.click_to_unmute()}</div>
                </div>
            </div>
        {/await}
    {/if}

    {#if isCurrentUserBlackListed}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
            class="absolute w-full h-full aspect-video mx-auto flex justify-center items-center bg-contrast/50 rounded-lg z-20 cursor-pointer"
            onclick={() => {
                if (userUuid !== undefined) blackListManager.cancelBlackList(userUuid);
                if (spaceUserId !== undefined) blackListManager.cancelBlackList(spaceUserId);
            }}
            data-testid="click-to-unblock"
        >
            <div class="text-center">
                <div class="text-lg text-white bold">{$LL.video.click_to_unblock()}</div>
            </div>
        </div>
    {/if}
</div>

<style>
    /* Spinner */
    .connecting-spinner {
        display: flex;
        left: calc(50% - 62px);
        top: calc(50% - 62px);

        /*width: 6rem;*/
        height: 75%;
        aspect-ratio: 1 / 1;
    }

    .connecting-spinner:after {
        content: " ";
        display: block;
        height: 100%;
        aspect-ratio: 1 / 1;
        border-radius: 50%;
        border: 6px solid var(--color-light-blue);
        border-color: var(--color-light-blue) transparent var(--color-light-blue) transparent;
        animation: connecting-spinner 1.2s linear infinite;
    }

    @keyframes connecting-spinner {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    .rtc-error {
        left: calc(50% - 68px);
        top: calc(42% - 68px);

        width: 8rem;
        height: 8rem;
    }

    .rtc-error:after {
        content: " ";
        display: block;
        width: 8rem;
        height: 8rem;
        border-radius: 50%;
        border: 6px solid #f00;
        animation: blinker 1s linear infinite;
    }

    @keyframes blinker {
        50% {
            opacity: 0;
        }
    }

    /* Black outline for mute icon SVG */
    /* Black outline for mute icon SVG */
    /* :global(.mute-icon-outline svg) {
        filter: drop-shadow(0 0 3px rgb(0, 0, 0)) drop-shadow(0 0 6px rgb(0, 0, 0))
            drop-shadow(0 0 9px rgb(0, 0, 0));
    } */

    /* Styles for mute icon SVG paths */
    :global(.mute-icon svg path) {
        stroke: #646464;
        stroke-width: 1px;
        stroke-dasharray: 2, 2;
        stroke-linejoin: round;
    }
</style>
